import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ArrowRight, ShieldCheck } from "lucide-react";
import { COMPANY, DISCLAIMERS, DISCLAIMER_INTRO } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const emailValid = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

export default function DisclaimerGate() {
  const navigate = useNavigate();
  const [agreed, setAgreed] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const formValid =
    form.firstName.trim() && form.lastName.trim() && emailValid(form.email.trim());
  const canContinue = agreed && formValid && !submitting;

  const handleChange = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    if (error) setError("");
  };

  const handleContinue = async () => {
    if (!agreed) return;
    if (!formValid) {
      setError("Please enter your first name, last name, and a valid email.");
      return;
    }
    setSubmitting(true);
    try {
      await fetch(`${API}/investor-leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: form.firstName.trim(),
          last_name: form.lastName.trim(),
          email: form.email.trim(),
        }),
      });
    } catch (e) {
      // Non-blocking: still allow access even if the log call fails.
    }
    sessionStorage.setItem("epic_agreed", "true");
    sessionStorage.setItem("epic_investor", JSON.stringify(form));
    setLeaving(true);
    setTimeout(() => navigate("/deck"), 650);
  };

  const inputClass =
    "w-full bg-epic-bg border border-epic-line focus:border-epic-accent outline-none px-4 py-3.5 font-mono text-sm text-epic-text placeholder:text-epic-sub/50 transition-colors duration-200";

  return (
    <div
      data-testid="disclaimer-gate"
      className="relative min-h-screen w-full bg-epic-bg flex flex-col overflow-hidden"
    >
      {/* red edge accent */}
      <div className="absolute top-0 left-0 right-0 h-px bg-epic-accent" />

      <AnimatePresence>
        {leaving && (
          <motion.div
            initial={{ scaleY: 0 }}
            animate={{ scaleY: 1 }}
            transition={{ duration: 0.65, ease: [0.76, 0, 0.24, 1] }}
            style={{ transformOrigin: "bottom" }}
            className="fixed inset-0 z-[100] bg-epic-accent flex items-center justify-center"
          >
            <span className="font-grotesk font-black uppercase tracking-tighter text-white text-4xl md:text-6xl">
              e<span className="text-epic-bg">P</span>IC
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="relative z-10 mx-auto w-full max-w-5xl px-5 md:px-10 flex-1 flex flex-col justify-center py-16 md:py-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-epic-sub mb-8">
            <ShieldCheck className="w-4 h-4 text-epic-accent" />
            Confidential — {COMPANY.round} & Equity Ownership · Accredited Investors Only
          </div>

          <h1 className="font-grotesk font-black uppercase tracking-tighter text-epic-text leading-[0.9] mb-6"
              style={{ fontSize: "clamp(2.25rem, 7vw, 4.5rem)" }}>
            Before you<br />
            <span className="text-epic-accent">continue.</span>
          </h1>

          <p className="font-mono text-sm md:text-base text-epic-sub max-w-2xl leading-relaxed mb-12">
            The following materials for {COMPANY.name} contain confidential financial
            projections intended solely for prospective accredited investors. Please provide
            your details and confirm the disclosures below to access the investment overview.
          </p>
        </motion.div>

        {/* Investor details */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="mb-4"
        >
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-epic-sub mb-4">
            Your Details
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input
              type="text"
              data-testid="investor-first-name"
              placeholder="First name"
              value={form.firstName}
              onChange={handleChange("firstName")}
              className={inputClass}
            />
            <input
              type="text"
              data-testid="investor-last-name"
              placeholder="Last name"
              value={form.lastName}
              onChange={handleChange("lastName")}
              className={inputClass}
            />
            <input
              type="email"
              data-testid="investor-email"
              placeholder="Email address"
              value={form.email}
              onChange={handleChange("email")}
              className={inputClass}
            />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="border border-epic-line bg-epic-surface"
        >
          <div className="max-h-[38vh] overflow-y-auto p-6 md:p-8 space-y-5">
            <p className="font-mono text-[12px] md:text-[13px] leading-relaxed text-epic-sub/80 border-b border-epic-line pb-5">
              {DISCLAIMER_INTRO}
            </p>
            {DISCLAIMERS.map((d, i) => (
              <div key={i} className="flex gap-4">
                <span className="font-mono text-[11px] text-epic-accent shrink-0 mt-0.5">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <p className="font-mono text-[12px] md:text-[13px] leading-relaxed text-epic-sub">
                  {d}
                </p>
              </div>
            ))}
          </div>

          <button
            type="button"
            data-testid="disclaimer-checkbox"
            onClick={() => setAgreed((v) => !v)}
            className="w-full flex items-center gap-4 border-t border-epic-line p-6 md:p-8 text-left group"
          >
            <span
              className={`shrink-0 w-6 h-6 border flex items-center justify-center transition-colors duration-200 ${
                agreed ? "bg-epic-accent border-epic-accent" : "border-epic-lineHover group-hover:border-epic-text"
              }`}
            >
              <AnimatePresence>
                {agreed && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Check className="w-4 h-4 text-white" strokeWidth={3} />
                  </motion.span>
                )}
              </AnimatePresence>
            </span>
            <span className="font-mono text-[12px] md:text-sm text-epic-text leading-relaxed">
              I confirm I am an accredited investor and agree to the disclosures above and to keep all materials confidential.
            </span>
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8"
        >
          {error && (
            <p data-testid="gate-error" className="font-mono text-[11px] text-epic-accent mb-4">
              {error}
            </p>
          )}
          <button
            type="button"
            data-testid="disclaimer-continue"
            onClick={handleContinue}
            disabled={!canContinue}
            className={`group inline-flex items-center gap-3 px-8 py-5 font-mono text-xs uppercase tracking-[0.15em] transition-[background-color,opacity,transform] duration-300 ${
              canContinue
                ? "bg-epic-accent text-white hover:bg-epic-accentHover active:scale-95 cursor-pointer"
                : "bg-epic-surface text-epic-sub border border-epic-line cursor-not-allowed opacity-60"
            }`}
          >
            {submitting ? "One moment…" : "Agree & View Investment Overview"}
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </motion.div>
      </div>
    </div>
  );
}
